import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  signal,
  HostListener,
  computed,
  effect,
  input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { TABLE_MOCK_DATA } from './table.mock';
import { TableHeaderComponent } from './table-header/table-header.component';
import { TablePaginatorComponent } from './table-paginator/table-paginator.component';
import { TableBodyComponent } from './table-body/table-body.component';
import { FilterOption, IconValidation, TableColumn } from './table.interface';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableHeaderComponent,
    TablePaginatorComponent,
    TableBodyComponent,
  ],
  styleUrls: ['./table.component.scss'],
  templateUrl: './table.component.html',
})
export class TableComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() columns: TableColumn[] = [];
  data = input<any[]>([]);
  pageSize = signal(10);
  @Input() isMocked = false;
  @Input() showSearch = true;
  @Input() showPagination = true;
  @Input() showSorting = true;
  @Input() showFilters = true;
  @Input() showCreateButton = true;
  @Input() showToggle = false;
  @Input() totalItems: number = 0;
  @Input() isServerSide: boolean = false;
  @Input() icon1 = '';
  @Input() icon2 = '';
  @Input() icon3 = '';
  @Input() icon1Tooltip: string = 'Acción 1';
  @Input() icon2Tooltip: string = 'Acción 2';
  @Input() icon3Tooltip: string = 'Acción 3';
  @Input() icon1Validation?: IconValidation;
  @Input() icon2Validation?: IconValidation;
  @Input() icon3Validation?: IconValidation;
  @Input() showIcon1Conditionally: boolean = false;
  @Input() icon1ConditionalField: string = 'status';
  @Input() icon1ConditionalValues: string[] = ['borrador'];
  @Input() createButtonText = '';;
  @Input() createButtonIcon = 'fi fi-rr-plus';
  @Input() clearFiltersSignal = signal(0);
  @Input() filterOptions: { [key: string]: FilterOption[] } = {};
  @Input() filterOptionsLoading: boolean = false;
  @Input() filterLabels: { [key: string]: string } = {};
  @Input() itemTypePaginationText = 'artículos';
  @Input() itemFormatPaginationText = '{start}-{end} de {total} artículos';
  @Input() firstColumnDark: boolean = false;
  @Input() resultsNotFoundText: string = 'No se encontraron resultados';
  @Input() set rowsPerPage(value: number) {
    this.pageSize.set(value);
  }
  @Input() enableRowClick: boolean = false;
  @Input() chipStyleClasses: { [key: string]: string } = {};

  @Output() searchChange = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<{ page: number; limit: number }>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() sortChange = new EventEmitter<{
    column: string;
    direction: 'asc' | 'desc';
  }>();
  @Output() iconAction1 = new EventEmitter<any>();
  @Output() iconAction2 = new EventEmitter<any>();
  @Output() iconAction3 = new EventEmitter<any>();
  @Output() toggleAction = new EventEmitter<{ row: any; checked: boolean }>();
  @Output() createClick = new EventEmitter<void>();
  @Output() filterChange = new EventEmitter<{ [key: string]: any }>();
  @Output() clearFiltersClick = new EventEmitter<void>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() dateFilterChange = new EventEmitter<{ column: string; startDate?: string; endDate?: string }>();
  @Output() multiSelectFilterChange = new EventEmitter<{ column: string; values: (string | number)[] }>();
  @Output() filterSearchChange = new EventEmitter<{ column: string; searchTerm: string }>();
  @ViewChild('tableContainer', { static: true })
  tableContainer!: ElementRef<HTMLElement>;

  private isHandlingToggle = false;
  searchControl = new FormControl('');
  showColumnShadow = false;
  showMobileFilters = false;

  currentPage = signal(1);
  sortColumn = signal<string | null>(null);
  sortDirection = signal<'asc' | 'desc'>('asc');
  appliedFilters = signal<{ [key: string]: any }>({});
  searchTerm = signal('');

  constructor() {
    effect(() => {
      if (this.clearFiltersSignal() > 0) this.handleClearFilters();
    });
  }

  ngOnInit() {
    this.searchControl.valueChanges.subscribe((value) => {
      this.searchChange.emit(value || '');
      this.currentPage.set(1);
      this.searchTerm.set(value || '');
    });
  }

  ngAfterViewInit() {
    this.checkScroll();
  }

  ngOnDestroy() {}
  @HostListener('window:resize')
  onResize() {
    this.checkScroll();
  }

  private checkScroll() {
    const el = this.tableContainer?.nativeElement;
    this.showColumnShadow = el && el.scrollWidth > el.clientWidth;
  }

  actualData = computed(() => (this.isMocked ? TABLE_MOCK_DATA : this.data()));

  visibleColumns = computed(() => this.columns.filter(col => !col.hidden));

  filteredData = computed(() => {
    if (this.isServerSide) return this.actualData();

    let result = [...this.actualData()];
    Object.entries(this.appliedFilters()).forEach(([key, value]) => {
      if (value) result = result.filter((row) => row[key] === value);
    });
    const search = this.searchTerm().toLowerCase().trim();
    if (search) {
      result = result.filter((row) =>
        this.columns.some((col) =>
          String(row[col.key] ?? '')
            .toLowerCase()
            .includes(search)
        )
      );
    }
    return result;
  });

  sortedData = computed(() => {
    if (this.isServerSide) return this.filteredData();

    const data = [...this.filteredData()];
    if (this.sortColumn()) {
      data.sort((a, b) => {
        const valA = a[this.sortColumn()!];
        const valB = b[this.sortColumn()!];
        if (valA < valB) return this.sortDirection() === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection() === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  });

  paginatedData = computed(() => {
    if (this.isServerSide) return this.sortedData();

    const start = (this.currentPage() - 1) * this.pageSize();
    return this.sortedData().slice(start, start + this.pageSize());
  });

  emptyRows = computed(() => {
    const diff = this.pageSize() - this.paginatedData().length;
    return diff > 0 ? Array(diff).fill(null) : [];
  });

  totalPages = computed(() =>
    this.isServerSide
      ? Math.ceil(this.totalItems / this.pageSize())
      : Math.ceil(this.filteredData().length / this.pageSize())
  );

  onSort(columnKey: string, direction: 'asc' | 'desc') {
    if (this.isServerSide) {
      this.currentPage.set(1);
    }
    this.sortColumn.set(columnKey);
    this.sortDirection.set(direction);
    this.sortChange.emit({ column: columnKey, direction });
  }

  onDateFilter(event: { column: string; startDate?: string; endDate?: string }) {
    this.dateFilterChange.emit(event);
    this.currentPage.set(1);
  }

  onMultiSelectFilter(event: { column: string; values: (string | number)[] }) {
    this.multiSelectFilterChange.emit(event);
    this.currentPage.set(1);
  }

  onFilterSearch(event: { column: string; searchTerm: string }) {
    this.filterSearchChange.emit(event);
  }

  goToPage(page: number) {
    const total = this.totalPages();
    if (page < 1) page = 1;
    if (page > total) page = total;
    this.currentPage.set(page);
    this.pageChange.emit({ page: this.currentPage(), limit: this.pageSize() });
  }

  onPageSizeChange(newSize: number) {
    this.pageSize.set(newSize);
    this.currentPage.set(1);
    this.pageSizeChange.emit(newSize);
    if (this.isServerSide) {
    this.pageChange.emit({ page: 1, limit: newSize });
  }
  }

  toggleRow(row: any) {
    row.toggleActive = !row.toggleActive;
    this.toggleAction.emit({ row, checked: row.toggleActive });
  }

  onFilterChange(filters: { [key: string]: any }) {
    this.appliedFilters.set(filters);
    this.currentPage.set(1);
    this.filterChange.emit(filters);
  }

  onEstadoChange(value: string) {
    const currentFilters = { ...this.appliedFilters() };
    currentFilters['estado'] = value || null;
    this.onFilterChange(currentFilters);
  }

  onTipoCargaChange(value: string) {
    const currentFilters = { ...this.appliedFilters() };
    currentFilters['tipoCarga'] = value || null;
    this.onFilterChange(currentFilters);
  }

  clearFilters() {
    this.handleClearFilters();
  }

  handleClearFilters() {
    this.searchControl.setValue('', { emitEvent: false });
    this.searchTerm.set('');
    this.appliedFilters.set({});
    this.currentPage.set(1);
    this.clearFiltersClick.emit();
    this.showMobileFilters = false;
  }

  getResultsText(): string {
    const total = this.isServerSide
      ? this.totalItems
      : this.filteredData().length;
    if (total === 0) return '0';
    const start = (this.currentPage() - 1) * this.pageSize() + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `${start}-${end} de ${total}`;
  }

  get totalFilteredItems(): number {
    return this.isServerSide ? this.totalItems : this.filteredData().length;
  }

  onCrearProducto() {
    this.createClick.emit();
  }

  toggleMobileFilters() {
    this.showMobileFilters = !this.showMobileFilters;
  }
}
